import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Button
} from "reactstrap";
import { withTranslation } from "react-i18next";

import { Link, useParams, useLocation } from 'react-router-dom';

import TiltCardRow from "./TiltCardRow"; // For Big Screen
import TopicRow from "./TopicRow"; // For Small Screen

import axiosInstance from "utils/axiosInstance";
import BreadcrumbAboveImage from "components/Common/BreadcrumbAboveImage";

import home4 from "assets/images/info/home4.png";

import { fetchThumbnailsWithCache } from "utils/thumbnails/fetchThumbnailsWithCache";

const TiltCardGrid = ({ t }) => {

  const { collectionId } = useParams();
  const location = useLocation();
  const { themeName, collectionName } = location.state || {};

  //--------------------------------------------------
  // States
  //--------------------------------------------------

  const [myInfoCollection, setMyInfoCollection] = useState([]);
  const [myInfoTopics, setMyInfoTopics] = useState([]);
  //const myInfoTopics = Array.from({ length: 40 }, (_, i) => `Card ${i + 1}`);
  const [thumbnailUrls, setThumbnailUrls] = useState({});

  const [collectionLoading, setCollectionLoading] = useState(true);
  const [dataFetchError, setDataFetchError] = useState(null);


  //--------------------------------------------------
  // Pages
  //--------------------------------------------------

  const [currentPage, setCurrentPage] = useState(0);

  const [cardsPerPage, setCardsPerPage] = useState(10);
  const totalPages = Math.ceil(myInfoTopics.length / cardsPerPage);

  const [cardsPerRow, setCardsPerRow] = useState(5);
  const updateCardsPerRow = () => {
    const width = window.innerWidth;
    // Extra small (mobile)
    if (width < 576) {
      setCardsPerPage(10);
      setCardsPerRow(1);
    }     
    // Small
    else if (width < 768) {
      setCardsPerPage(6);
      setCardsPerRow(2);
    }
    // Medium
    else if (width < 1190) {
      setCardsPerPage(6);
      setCardsPerRow(3); 
    } 
    // Large
    else if (width < 1380) {
      setCardsPerPage(8);
      setCardsPerRow(4); 
    }
    // Extra large
    else {
      setCardsPerPage(10);
      setCardsPerRow(5);
    }  
  };

  useEffect(()=>{
    setCurrentPage(0);
  },[cardsPerPage])

  useEffect(() => {
    updateCardsPerRow(); // initial run
    window.addEventListener('resize', updateCardsPerRow);
    return () => window.removeEventListener('resize', updateCardsPerRow);
  }, []);

  const paginatedCards = myInfoTopics.slice(
    currentPage * cardsPerPage,
    (currentPage + 1) * cardsPerPage
  );

  const rows = [];
  for (let i = 0; i < paginatedCards.length; i += cardsPerRow) {
    rows.push(paginatedCards.slice(i, i + cardsPerRow));
  }
  
  

  //--------------------------------------------------
  // On mount, load collection and topics
  //--------------------------------------------------

  const fetchData = async () => {
    try {
      setCollectionLoading(true);
      setDataFetchError(null);

      // Load Collection and Topics
      const collectionResponse = await axiosInstance.get(
        `/knovia/myInfo/${collectionId}/getCollectionInfo`
      );
      console.log('Collection Loaded:', collectionResponse.data);
      setMyInfoCollection(collectionResponse.data);

      const topicResponse = await axiosInstance.get(
        `/knovia/myInfo/${collectionId}/getTopicsInCollection`
      );
      console.log('topics in Collection Loaded:', topicResponse.data);

      // Sort by `order` before setting state
      const sortedTopics = [...topicResponse.data].sort(
        (a, b) => a.order - b.order
      );
      setMyInfoTopics(sortedTopics);

      return sortedTopics;
    } catch (err) {
      console.error(err);
      setDataFetchError("Failed to load data.");
    } finally {
      setCollectionLoading(false);
    }
  };

  const fetchThumbnails = (sortedTopics) => {
    console.log("start fetching thumbnails");

    fetchThumbnailsWithCache(
      sortedTopics,
      "infoTopic",
      "sizeMedium",
      ({ id, url }) => {
        setThumbnailUrls(prev => ({
          ...prev,
          [id]: url
        }));
      },
      () => {
        console.log("✅ All thumbnails finished loading");
        // Optional: set loading state to false
      }
    );
  };

  useEffect(() => {
    (async () => {
      const sortedTopics = await fetchData();
      if (sortedTopics?.length > 0) {
        fetchThumbnails(sortedTopics);
      }
    })();
  }, [collectionId]);



  //--------------------------------------------------
  // Return
  //--------------------------------------------------

  return (

    <div className="page-content topic-list"
      style={{ 
        backgroundImage: `url(${home4})`,
      }}
    >

      <Container fluid>
        <BreadcrumbAboveImage
          title={t("My Info")}
          breadcrumbItems={[
            { title: t("我的 AI 應用主頁"), to: "/myInfo/dashboard" },
            { title: t("..."), to: "/myInfo/navigateThemes" },
            { 
              title: t(`${myInfoCollection?.theme?.label || themeName}`), 
              to: `/myInfo/themes/${myInfoCollection?.theme?._id || ""}`
            },
            { title: t(`${myInfoCollection?.label || collectionName}`)}
          ]}
        />

          <Card className="topic-list-card">
            <CardBody>

              <Row
                style={{ minHeight: "580px" }}
              >
                <Col xs={12}>
                  {/* Big Screen Cards */}
                  <div className="d-none d-sm-flex flex-column align-items-center"
                    style={{ marginBottom: "35px" }}
                  >
                    {rows.map((rowCards, i) => (
                      <TiltCardRow 
                        key={i} 
                        cardsPerRow={cardsPerRow} 
                        cards={rowCards}
                        thumbnailUrls={thumbnailUrls}
                        themeName={myInfoCollection?.theme?.label || themeName}
                        collectionName={myInfoCollection?.label || collectionName}
                      />
                    ))}
                  </div>

                  {/* Small Screen Rows */}
                  <div className="d-flex d-sm-none flex-column align-items-center"
                    style={{ paddingTop: "30px", paddingBottom: "25px" }}
                  >
                    {rows.map((rowCards, i) => (
                      <TopicRow 
                        key={i} 
                        cardsPerRow={cardsPerRow} 
                        cards={rowCards} 
                        thumbnailUrls={thumbnailUrls}
                        themeName={myInfoCollection?.theme?.label || themeName}
                        collectionName={myInfoCollection?.label || collectionName}
                      />
                    ))}
                  </div>
                </Col>
              </Row>

              <Row className="justify-content-center"
                style={{ marginBottom:"35px" }}
              >
                <Col xs="auto">
                  <Button
                    color="secondary"
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                  >
                    Previous
                  </Button>
                </Col>
                <Col xs="auto">
                  <Button
                    color="primary"
                    disabled={currentPage === totalPages - 1}
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                  >
                    Next
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

      </Container>
    </div>
  );
};

export default withTranslation()(TiltCardGrid);


